import * as types from '../actionTypes';

export const KeywordVisibilityFilters = {
  SHOW_ALL_UNCATEGORIZED: 'SHOW_ALL_UNCATEGORIZED',
  SHOW_ACTIVE: 'SHOW_ACTIVE',
  SHOW_INACTIVE: 'SHOW_INACTIVE',
};

const initialState = {
  allKeywords: [],
  columnDefinitions: [],
  importedKeywordsRaw: '',
  keywordVisibilityFilter: KeywordVisibilityFilters.SHOW_ACTIVE,
  filterChips: [],
  regexError: null,
  // New structure for specific column filters (used by UI, not directly by selector for chips)
  volumeFilter: { min: '', max: '' }, // Stores UI input for volume
  kdFilter: { min: '', max: '' },     // Stores UI input for KD
  cpcFilter: { min: '', max: '' },     // Stores UI input for CPC
  nGramMatchMode: 'wholeWord',
  superSelectMode: false,
  selectedKeywordIds: [],
  lastSelectedKeywordId: null,
};

export const getInitialKeywordState = () => ({ ...initialState });

const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const parseNumericalValue = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }
  const cleanedValue = String(value).replace(/,/g, '');
  const num = parseFloat(cleanedValue);
  return isNaN(num) ? null : num;
};


const keywordReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.RESTORE_STATE:
      if (action.payload && action.payload.keywords) {
        const restoredKeywordsState = action.payload.keywords;
        const defaultKwValues = {
          isCategorized: false, isActive: true, isDeleted: false, data: {}
        };
        const allKeywords = (Array.isArray(restoredKeywordsState.allKeywords)
          ? restoredKeywordsState.allKeywords
          : []
        ).map(kw => ({
            ...defaultKwValues,
            ...kw,
            data: (typeof kw.data === 'object' && kw.data !== null) ? kw.data : {},
            normalizedText: kw.normalizedText || normalizeText(kw.originalText || ''),
          }));

        return {
          ...initialState,
          ...restoredKeywordsState,
          allKeywords,
          columnDefinitions: restoredKeywordsState.columnDefinitions || [],
          filterChips: restoredKeywordsState.filterChips || [],
          regexError: restoredKeywordsState.regexError || null,
          // Restore specific column filter UI states
          volumeFilter: restoredKeywordsState.volumeFilter || { ...initialState.volumeFilter },
          kdFilter: restoredKeywordsState.kdFilter || { ...initialState.kdFilter },
          cpcFilter: restoredKeywordsState.cpcFilter || { ...initialState.cpcFilter },
          nGramMatchMode: restoredKeywordsState.nGramMatchMode || initialState.nGramMatchMode,
          superSelectMode: restoredKeywordsState.superSelectMode || false,
          selectedKeywordIds: restoredKeywordsState.selectedKeywordIds || [],
          lastSelectedKeywordId: restoredKeywordsState.lastSelectedKeywordId || null,
        };
      }
      return state;

    case types.SET_RAW_IMPORTED_KEYWORDS:
      return { ...state, importedKeywordsRaw: action.payload };

    case types.IMPORT_KEYWORDS: {
      const lines = action.payload.split('\n').filter(line => line.trim() !== '');
      if (lines.length === 0) return { ...initialState, importedKeywordsRaw: action.payload };
      const headerLine = lines[0];
      const rawHeaders = headerLine.split(/[	,]/);
      const actualHeaders = rawHeaders.slice(1, 11);
      const newColumnDefinitions = actualHeaders.map((header, index) => ({
        id: `col_${index}`, headerName: header.trim(), type: 'string', displayOrder: index,
      }));
      const dataRows = lines.slice(1);
      const newKeywords = dataRows.map((line, rowIndex) => {
          const values = line.split(/[	,]/);
          const originalText = values[0] ? values[0].trim() : '';
          if (!originalText) return null;
          const dynamicData = {};
          newColumnDefinitions.forEach((colDef, colIndex) => {
            dynamicData[colDef.id] = values[colIndex + 1] ? values[colIndex + 1].trim() : null;
          });
          return {
            id: `kw-${Date.now()}-${rowIndex}-${Math.random().toString(36).substr(2, 9)}`,
            originalText, normalizedText: normalizeText(originalText), data: dynamicData,
            isCategorized: false, isActive: true, isDeleted: false,
          };
        }).filter(kw => kw !== null);
      return {
        ...initialState, allKeywords: newKeywords, columnDefinitions: newColumnDefinitions,
        importedKeywordsRaw: action.payload,
      };
    }

    case types.SET_COLUMN_FILTER: { // Renamed from SET_NUMERICAL_FILTER_FIELD
      const { columnName, min, max } = action.payload; // Expecting 'volume', 'kd', 'cpc' for columnName
      const filterName = `${columnName}Filter`;
      if (state.hasOwnProperty(filterName)) {
        return { ...state, [filterName]: { min, max } };
      }
      return state;
    }

    // ... (other action handlers: ASSIGN_KEYWORD_TO_FOLDER, ASSIGN_MULTIPLE_KEYWORDS_TO_FOLDER, etc. remain same) ...
    case types.ASSIGN_KEYWORD_TO_FOLDER:
      return {
        ...state,
        allKeywords: state.allKeywords.map(kw =>
          kw.id === action.payload.keywordItem.id ? { ...kw, isCategorized: true } : kw
        ),
        selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds.filter(id => id !== action.payload.keywordItem.id) : [],
        lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
      };

    case types.ASSIGN_MULTIPLE_KEYWORDS_TO_FOLDER: {
      const { keywordObjects } = action.payload;
      const idsToCategorize = keywordObjects.map(kw => kw.id);
      return {
        ...state,
        allKeywords: state.allKeywords.map(kw =>
          idsToCategorize.includes(kw.id) ? { ...kw, isCategorized: true } : kw
        ),
        selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds.filter(id => !idsToCategorize.includes(id)) : [],
        lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
      };
    }

    case types.TOGGLE_KEYWORD_ACTIVE:
      return {
        ...state,
        allKeywords: state.allKeywords.map(kw =>
          kw.id === action.payload ? { ...kw, isActive: !kw.isActive } : kw
        ),
      };

    case types.MARK_KEYWORD_DELETED:
      const deletedId = action.payload;
      return {
        ...state,
        allKeywords: state.allKeywords.map(kw =>
          kw.id === deletedId ? { ...kw, isDeleted: true, isActive: false } : kw
        ),
        selectedKeywordIds: state.selectedKeywordIds.filter(id => id !== deletedId),
        lastSelectedKeywordId: state.lastSelectedKeywordId === deletedId ? null : state.lastSelectedKeywordId,
      };

    case types.SET_KEYWORD_VISIBILITY_FILTER:
      return {
        ...state,
        keywordVisibilityFilter: action.payload,
        selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds : [],
        lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
      };

    case types.ADD_FILTER_CHIP:
      if (action.payload.type === 'regex') {
        try {
          new RegExp(action.payload.params.regexString);
          return {
            ...state,
            filterChips: [...state.filterChips, action.payload],
            regexError: null,
            selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds : [],
            lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
          };
        } catch (e) {
          return { ...state, regexError: e.message };
        }
      }
      return {
        ...state,
        filterChips: [...state.filterChips, action.payload],
        selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds : [],
        lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
      };

    case types.REMOVE_FILTER_CHIP: {
      const chipToRemove = state.filterChips.find(chip => chip.id === action.payload);
      const newFilterChips = state.filterChips.filter(chip => chip.id !== action.payload);
      let newRegexError = state.regexError;
      if (chipToRemove && chipToRemove.type === 'regex') {
        if (!newFilterChips.some(chip => chip.type === 'regex')) {
          newRegexError = null;
        }
      }
      return {
        ...state,
        filterChips: newFilterChips,
        regexError: newRegexError,
        selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds : [],
        lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
      };
    }

    case types.CLEAR_ALL_FILTER_CHIPS:
      return {
        ...state,
        filterChips: [],
        regexError: null,
        selectedKeywordIds: state.superSelectMode ? state.selectedKeywordIds : [],
        lastSelectedKeywordId: state.superSelectMode ? state.lastSelectedKeywordId : null,
      };

    case types.SET_NGRAM_MATCH_MODE:
      return { ...state, nGramMatchMode: action.payload };

    case types.TOGGLE_SUPER_SELECT_MODE:
      return {
        ...state,
        superSelectMode: !state.superSelectMode,
      };

    case types.SET_SELECTED_KEYWORDS:
      return {
        ...state,
        selectedKeywordIds: action.payload,
        lastSelectedKeywordId: action.payload.length > 0 ? action.payload[action.payload.length - 1] : null,
      };

    case types.TOGGLE_KEYWORD_SELECTION: {
      const keywordId = action.payload;
      const newSelectedIds = state.selectedKeywordIds.includes(keywordId)
        ? state.selectedKeywordIds.filter(id => id !== keywordId)
        : [...state.selectedKeywordIds, keywordId];
      return {
        ...state,
        selectedKeywordIds: newSelectedIds,
        lastSelectedKeywordId: keywordId,
      };
    }
    case types.RANGE_SELECT_KEYWORDS: {
      const { clickedKeywordId, allDisplayedKeywordIds } = action.payload;
      const { lastSelectedKeywordId } = state;
      let newSelectedIds = [];

      if (!lastSelectedKeywordId || !allDisplayedKeywordIds.includes(lastSelectedKeywordId)) {
        newSelectedIds = [clickedKeywordId];
      } else {
        const lastIdx = allDisplayedKeywordIds.indexOf(lastSelectedKeywordId);
        const clickedIdx = allDisplayedKeywordIds.indexOf(clickedKeywordId);
        const start = Math.min(lastIdx, clickedIdx);
        const end = Math.max(lastIdx, clickedIdx);
        newSelectedIds = allDisplayedKeywordIds.slice(start, end + 1);
      }
      return { ...state, selectedKeywordIds: newSelectedIds };
    }

    case types.SET_KEYWORDS:
      const defaultKwValuesForSet = {
        isCategorized: false, isActive: true, isDeleted: false, data: {}
      };
      return {
        ...state,
        allKeywords: action.payload.map(kw => ({
          ...defaultKwValuesForSet,
          ...kw,
          originalText: kw.originalText || '',
          normalizedText: kw.normalizedText || normalizeText(kw.originalText || ''),
          id: kw.id || `kw-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          data: (typeof kw.data === 'object' && kw.data !== null) ? kw.data : {},
        })),
        selectedKeywordIds: [],
        lastSelectedKeywordId: null,
      };
    default:
      return state;
  }
};

export default keywordReducer;
